import { useEffect, useState } from 'react';
import { CmsMarkdownContent } from '@/components/cms/CmsMarkdownContent';
import { P } from '@/components/ui/typography';
import { Skeleton } from '@/components/ui/skeleton';
import {
    getArticleBlocksMarkdown,
    strapiService,
    type StrapiPageModel,
} from '@/services/StrapiService';
import { ALLOWED_CMS_PAGE_SLUGS } from '@/pages/Cms/cmsPageSlug';

type CmsPageBodyBySlugProps = {
    slug: string;
    className?: string;
};

function getPageMarkdownSource(data: Record<string, unknown>): string | null {
    const direct = data.body ?? data.description ?? data.content;
    if (typeof direct === 'string' && direct.trim() !== '') {
        return direct;
    }
    const fromBlocks = getArticleBlocksMarkdown(data);
    if (fromBlocks) {
        return fromBlocks;
    }
    return null;
}

/**
 * Fetches a Strapi `pages` entry by slug and renders the body in a dialog-friendly layout
 * (no `DesktopContainer`). Last-modified / title lines from CMS live in the markdown body.
 */
export const CmsPageBodyBySlug = ({ slug, className }: CmsPageBodyBySlugProps) => {
    const [page, setPage] = useState<StrapiPageModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!slug || !ALLOWED_CMS_PAGE_SLUGS.includes(slug)) {
            setLoading(false);
            setPage(null);
            setError(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        strapiService
            .getPageBySlug(slug)
            .then((data) => {
                if (!cancelled) {
                    setPage(data);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load page');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (loading) {
        return (
            <div className={className ? `flex flex-col gap-3 min-h-0 ${className}` : 'flex flex-col gap-3 min-h-0'}>
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
            </div>
        );
    }

    if (error) {
        return (
            <P variant="type-body-default" className="text-destructive">
                {error}
            </P>
        );
    }

    if (!page) {
        return (
            <P variant="type-body-default">
                This page could not be loaded.
            </P>
        );
    }

    const data = page.data ?? {};
    const source = getPageMarkdownSource(data);

    const bodyBlock = (() => {
        if (source != null) {
            return <CmsMarkdownContent markdown={source} />;
        }
        if (data.body != null && typeof data.body === 'object') {
            return (
                <pre className="type-body-default whitespace-pre-wrap overflow-x-auto rounded-md border border-border bg-muted p-4 text-sm">
                    {JSON.stringify(data.body, null, 2)}
                </pre>
            );
        }
        return (
            <P variant="type-body-default" className="whitespace-pre-wrap text-muted-foreground">
                No content is available for this page yet.
            </P>
        );
    })();

    return <div className={className ? `min-h-0 overflow-y-auto pr-1 ${className}` : 'min-h-0 overflow-y-auto pr-1'}>{bodyBlock}</div>;
};
